import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Download } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Separator } from '@/shared/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { instrumentPath } from '@/shared/lib/routes'
import type { WebInstrumentCalendarView, WebInstrumentServiceReport } from '@/shared/types/web'
import { t } from '@/shared/lib/i18n'
import type { SharedProps } from '@/shared/types/SharedProps'
import { fetchInertiaPartial } from '@/shared/lib/fetchInertiaPartial'
import { CommentsPanel } from '@/features/comments/CommentsPanel'
import InstrumentCalendarViewPanel from './InstrumentCalendarViewPanel'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

const INERTIA_PARTIAL_COMPONENT = 'Instruments/Show'

interface Props {
  instrumentId: number
  instrumentLabel: string
  serialNumber: string | null
  reportsCalendar: WebInstrumentCalendarView | null
  reportsSiteToday?: string
  serviceReports: WebInstrumentServiceReport[]
  isActive: boolean
  /**
   * Forwarded to InstrumentCalendarViewPanel's onAfterReview — fired after a compliance
   * review action or a task-log void/delete, so the parent page (Instruments/Show.tsx) can
   * refresh its own stale props (e.g. currentSchedule — AC3).
   */
  onAfterReview?: () => void
  /**
   * Forwarded to InstrumentCalendarViewPanel's refreshSignal — bump to silently re-fetch
   * the current month after a task log is submitted or voided from the Tasks tab.
   */
  calendarRefreshSignal?: number
}

// The Service Reports list is scoped by the same reports_from/reports_to window as the calendar
// grid, so every in-page calendar fetch also asks for `serviceReports` and hands it back — otherwise
// the list keeps showing the month it was server-rendered for after the user changes month.
export const buildInstrumentFetchFn =
  (
    instrumentId: number,
    pageVersion: string | null | undefined,
    onServiceReports?: (reports: WebInstrumentServiceReport[]) => void,
  ) =>
  async (
    from: string,
    to: string,
    taskIds: number[],
  ): Promise<WebInstrumentCalendarView | null> => {
    const url = new URL(instrumentPath(instrumentId), window.location.origin)
    url.searchParams.set('reports_from', from)
    url.searchParams.set('reports_to', to)
    if (taskIds.length > 0) url.searchParams.set('reports_task_ids', taskIds.join(','))

    const props = await fetchInertiaPartial<{
      reportsCalendar: WebInstrumentCalendarView
      serviceReports?: WebInstrumentServiceReport[]
    }>(url, {
      component: INERTIA_PARTIAL_COMPONENT,
      only: ['reportsCalendar', 'serviceReports'],
      version: pageVersion,
    })
    if (!props) return null

    if (props.serviceReports) onServiceReports?.(props.serviceReports)
    return props.reportsCalendar ?? null
  }

// ── Detail modal ─────────────────────────────────────────────────────────────

interface DetailModalProps {
  report: WebInstrumentServiceReport | null
  onClose: () => void
}

const ServiceReportDetailModal = ({ report, onClose }: DetailModalProps) => {
  const { formatDateTime } = useSiteDateTime()

  if (!report) return null

  return (
    <Dialog open={report !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] sm:max-w-[38rem] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('instruments.reports.serviceReports.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="min-w-0 space-y-5 text-sm">
          {report.problemDescription && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('instruments.reports.serviceReports.descriptionLabel')}
              </p>
              <p className="whitespace-pre-wrap break-words">{report.problemDescription}</p>
            </div>
          )}

          {report.attachments.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('instruments.reports.serviceReports.attachmentsLabel')}
              </p>
              <ul className="space-y-1.5">
                {report.attachments.map((attachment) => (
                  <li key={attachment.id}>
                    <a
                      href={attachment.downloadHref}
                      download
                      className="inline-flex items-center gap-1.5 font-medium text-primary underline-offset-2 hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {attachment.attachmentName}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-muted-foreground">
              <span className="font-medium">
                {t('instruments.reports.serviceReports.completedAt')}
              </span>{' '}
              {formatDateTime(report.completedAt)}
            </p>
            {report.completedByName && (
              <p className="text-muted-foreground">
                <span className="font-medium">
                  {t('instruments.reports.serviceReports.completedBy')}
                </span>{' '}
                {report.completedByName}
              </p>
            )}
          </div>

          <Separator />

          <CommentsPanel
            comments={report.comments}
            commentableType="InstrumentServiceReport"
            commentableId={report.id}
            compact
            disableEdit
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const ReportsTabContent = ({
  instrumentId,
  instrumentLabel,
  serialNumber,
  reportsCalendar,
  reportsSiteToday,
  serviceReports,
  isActive,
  onAfterReview,
  calendarRefreshSignal,
}: Props) => {
  const { formatDateTime } = useSiteDateTime()
  const { version } = usePage<SharedProps>()
  const [localReports, setLocalReports] = useState(serviceReports)
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null)

  const selectedReport = selectedReportId
    ? (localReports.find((r) => r.id === selectedReportId) ?? null)
    : null

  // Keep in sync when Inertia reloads props (e.g. after a new report or comment is created).
  // selectedReport is derived from localReports so it updates automatically.
  useEffect(() => {
    setLocalReports(serviceReports)
  }, [serviceReports])

  return (
    <div className="space-y-6">
      <InstrumentCalendarViewPanel
        instrumentId={instrumentId}
        instrumentLabel={instrumentLabel}
        serialNumber={serialNumber}
        initialCalendarData={reportsCalendar}
        siteToday={reportsSiteToday}
        fetchCalendarData={buildInstrumentFetchFn(instrumentId, version, setLocalReports)}
        isActive={isActive}
        onAfterReview={onAfterReview}
        refreshSignal={calendarRefreshSignal}
        explicitTaskFilterApply
      />

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">
          {t('instruments.reports.serviceReports.sectionTitle')}
        </h3>

        {localReports.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('instruments.reports.serviceReports.empty')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('instruments.reports.serviceReports.table.description')}</TableHead>
                <TableHead>{t('instruments.reports.serviceReports.table.completedBy')}</TableHead>
                <TableHead className="text-right">
                  {t('instruments.reports.serviceReports.table.date')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {localReports.map((report) => (
                <TableRow
                  key={report.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedReportId(report.id)}
                >
                  <TableCell className="max-w-xs truncate">
                    {report.problemDescription || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{report.completedByName ?? '—'}</TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatDateTime(report.completedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ServiceReportDetailModal report={selectedReport} onClose={() => setSelectedReportId(null)} />
    </div>
  )
}

export default ReportsTabContent
